import { useEffect, RefObject } from 'react';
import { getKeyboardFocusableElements } from '@nebula/helpers';

/** True when `element`, and every ancestor up to `container`, is actually displayed. */
const isDisplayed = (element: HTMLElement, container: HTMLElement): boolean => {
  // Walks ancestors because `display: none` on a parent does not change a child's own
  // computed style — the hidden close button still reports `display: flex`.
  for (let node: HTMLElement | null = element; node; node = node.parentElement) {
    const { display, visibility } = globalThis.getComputedStyle(node);
    if (display === 'none' || visibility === 'hidden') return false;
    if (node === container) break;
  }

  return true;
};

/**
 * The dialog keeps the mobile sheet and the desktop card in the DOM at the same time and
 * hides one of them per breakpoint, so the close button that is hidden must be skipped.
 */
const getTabStops = (container: HTMLElement): HTMLElement[] =>
  (getKeyboardFocusableElements(container) as HTMLElement[]).filter(element => isDisplayed(element, container));

/** Moves focus one step through the dialog's tab stops, wrapping round at both ends. */
const focusNextTabStop = (container: HTMLElement, backwards: boolean) => {
  const stops = getTabStops(container);

  if (stops.length === 0) {
    container.focus();
    return;
  }

  // -1 means focus is on the dialog itself, so start from the end we are arriving from.
  const current = stops.indexOf(document.activeElement as HTMLElement);
  const start = backwards ? 0 : stops.length - 1;
  const from = current === -1 ? start : current;
  const step = backwards ? -1 : 1;

  stops[(from + step + stops.length) % stops.length].focus();
};

/**
 * Standard modal dialog focus behaviour, in three parts:
 *
 * 1. On open, move focus into the dialog. `aria-modal` hides the rest of the page from a
 *    screen reader but does not move focus, so without this the dialog opens silently.
 * 2. While open, keep Tab inside it. `aria-modal` does not affect the Tab key either, so
 *    focus would otherwise land on content hidden behind the overlay.
 * 3. On close, hand focus back to whatever opened it.
 *
 * `containerRef` must point at an element with `tabIndex={-1}`, so that step 1 can focus it.
 */
const useDialogFocus = (containerRef: RefObject<HTMLElement | null>, onEscape: () => void) => {
  useEffect(() => {
    const launchElement = document.activeElement as HTMLElement | null;
    containerRef.current?.focus();

    // Restoring on unmount covers every way the dialog can close, not just one handler.
    return () => {
      if (launchElement?.isConnected) {
        launchElement.focus();
      }
    };
  }, [containerRef]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const container = containerRef.current;
      if (!container) return;

      if (event.key === 'Escape') {
        onEscape();
      } else if (event.key === 'Tab') {
        event.preventDefault();
        focusNextTabStop(container, event.shiftKey);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [containerRef, onEscape]);
};

export default useDialogFocus;
